import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown, Volume2, VolumeX } from 'lucide-react';

interface FAQAccordionItemProps {
  question: string;
  answer: React.ReactNode;
  isOpen: boolean;
  onToggle: () => void;
  index: number;
  language?: string;
}

let audioCtx: AudioContext | null = null;
const playClickSound = () => {
  try {
    if (!audioCtx) {
      const AudioContextClass = window.AudioContext || (window as any).webkitAudioContext;
      if (!AudioContextClass) return;
      audioCtx = new AudioContextClass();
    }
    
    if (audioCtx.state === 'suspended') {
      audioCtx.resume();
    }

    const osc = audioCtx.createOscillator();
    const gainNode = audioCtx.createGain();

    osc.connect(gainNode);
    gainNode.connect(audioCtx.destination);

    osc.type = 'sine';
    osc.frequency.setValueAtTime(400, audioCtx.currentTime);
    osc.frequency.exponentialRampToValueAtTime(200, audioCtx.currentTime + 0.05);

    gainNode.gain.setValueAtTime(0.015, audioCtx.currentTime);
    gainNode.gain.exponentialRampToValueAtTime(0.001, audioCtx.currentTime + 0.05);

    osc.start(audioCtx.currentTime);
    osc.stop(audioCtx.currentTime + 0.05);
  } catch (e) {
    // Ignore silently
  }
};

const FAQAccordionItem: React.FC<FAQAccordionItemProps> = ({
  question,
  answer,
  isOpen,
  onToggle,
  index,
  language = 'en'
}) => {
  const [isSpeaking, setIsSpeaking] = useState(false);
  const textRef = useRef<HTMLParagraphElement>(null);

  const stopSpeaking = () => {
    if (window.speechSynthesis) {
      window.speechSynthesis.cancel();
    }
    setIsSpeaking(false);
  };

  useEffect(() => {
    if (!isOpen) stopSpeaking();
  }, [isOpen]);

  const handleSpeak = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!window.speechSynthesis) return;
    
    if (isSpeaking) {
      stopSpeaking();
      return;
    }

    const answerText = textRef.current?.innerText || '';
    const fullText = question + '. ' + answerText;
    
    const utterance = new SpeechSynthesisUtterance(fullText);
    utterance.lang = language === 'vi' ? 'vi-VN' : 'en-US';
    
    utterance.onend = () => setIsSpeaking(false);
    utterance.onerror = () => setIsSpeaking(false);

    setIsSpeaking(true);
    window.speechSynthesis.speak(utterance);
  };

  const handleToggle = () => {
    playClickSound();
    onToggle();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleToggle();
    }
  };

  return (
    <div
      className="bg-white/90 dark:bg-gray-800/90 backdrop-blur-sm rounded-xl shadow-lg
                 border-2 border-gray-200 dark:border-gray-700
                 hover:border-blue-400 dark:hover:border-blue-500
                 transition-all duration-300"
      data-aos="fade-up"
      data-aos-delay={index * 100}
    >
      <div
        id={`faq-btn-${index}`}
        role="button"
        tabIndex={0}
        aria-expanded={isOpen}
        aria-controls={`faq-content-${index}`}
        onClick={handleToggle}
        onKeyDown={handleKeyDown}
        className={`w-full flex items-center justify-between p-5 text-left
                   hover:bg-gray-50 dark:hover:bg-gray-700/50 cursor-pointer outline-none focus-visible:ring-2 focus-visible:ring-blue-500
                   transition-all duration-300 rounded-xl
                   ${isOpen ? 'bg-blue-50 dark:bg-blue-900/30' : ''}`}
      >
        <div className="flex items-center gap-4 flex-1">
          <span
            className={`flex items-center justify-center w-8 h-8 rounded-full
                       font-bold text-sm shrink-0
                       ${isOpen
                         ? 'bg-blue-600 text-white'
                         : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300'}`}
          >
            {index + 1}
          </span>
          <h3
            className={`text-lg font-semibold
                       ${isOpen
                         ? 'text-blue-600 dark:text-blue-400'
                         : 'text-gray-800 dark:text-white'}`}
          >
            {question}
          </h3>
        </div>
        <div className="flex items-center gap-2 shrink-0 ml-4">
          <button 
            onClick={handleSpeak}
            className={`p-2 rounded-full transition-all text-gray-400 hover:text-blue-500 hover:bg-blue-100 dark:hover:bg-blue-900/50
              ${isOpen ? 'opacity-100 scale-100' : 'opacity-0 scale-95 pointer-events-none'}`}
            title={isSpeaking ? (language === 'vi' ? 'Dừng đọc' : 'Stop') : (language === 'vi' ? 'Đọc văn bản' : 'Read aloud')}
          >
            {isSpeaking ? <VolumeX size={20} className="text-blue-500" /> : <Volume2 size={20} />}
          </button>
          <ChevronDown
            className={`w-6 h-6 transition-transform duration-300
                       ${isOpen
                         ? 'rotate-180 text-blue-600 dark:text-blue-400'
                         : 'text-gray-500 dark:text-gray-400'}`}
          />
        </div>
      </div>

      <div
        id={`faq-content-${index}`}
        role="region"
        aria-labelledby={`faq-btn-${index}`}
        className={`overflow-hidden transition-all duration-500 ease-in-out
                   ${isOpen ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'}`}
      >
        <div className="px-5 pb-5 pt-2">
          <div className="pl-12">
            <p ref={textRef} className="text-gray-700 dark:text-gray-300 leading-relaxed">
              {answer}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FAQAccordionItem;
